import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const accessToken = useMemo(() => {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(hash || window.location.search);
    return params.get('access_token') || params.get('token') || '';
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!accessToken) {
      toast({
        variant: 'destructive',
        title: 'Link inválido',
        description: 'Solicite um novo link de recuperação.',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        variant: 'destructive',
        title: 'Senha inválida',
        description: 'A senha deve ter pelo menos 6 caracteres.',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        variant: 'destructive',
        title: 'Senhas diferentes',
        description: 'Confirme a nova senha para continuar.',
      });
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/password-reset', {
        access_token: accessToken,
        password,
      });
      toast({
        title: 'Senha atualizada',
        description: 'Entre novamente usando sua nova senha.',
      });
      navigate('/login');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar senha',
        description: error.response?.data?.detail || 'Solicite um novo link de recuperação.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <Card className="relative w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <KeyRound className="h-8 w-8 text-primary" strokeWidth={1.5} />
          </div>
          <CardTitle className="text-3xl font-bold tracking-tight">Nova senha</CardTitle>
          <CardDescription className="text-base">
            Defina uma nova senha para acessar sua conta.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!accessToken && (
            <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Link de recuperação inválido ou expirado.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova senha</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={6}
                className="h-11"
                data-testid="reset-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar senha</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={6}
                className="h-11"
                data-testid="reset-password-confirm-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 rounded-full shadow-lg hover:shadow-xl"
              disabled={loading || !accessToken}
              data-testid="reset-password-submit-button"
            >
              {loading ? 'Atualizando...' : 'Atualizar senha'}
            </Button>
          </form>

          <div className="mt-6">
            <Button asChild variant="ghost" className="w-full">
              <Link to="/login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar para login
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
